/**
 * 관리자 작업현황 대시보드 (Phase 1) — admin 전용.
 *
 * 기능:
 *   - 기간 필터(from/to) + 프리셋(전체 · 최근 7일 · 최근 30일)
 *   - 작업자별 net 집계 테이블 (1차 검수완료 / 2차 확정 / 합계)
 *   - 행 클릭 시 아래로 펼쳐지는 드릴다운 — 탭 [의뢰 / 작업] (읽기전용, 상세로 링크)
 *
 * 집계·격리 정책은 서버(require_admin + AuditLog net 판정)와 일치. 삭제된
 * 사용자(user_id=null)는 드릴다운 대상이 없어 펼치기를 비활성화한다.
 */

import { useMemo, useState } from "react";
import { AlertCircle, ChevronDown, ChevronRight, Loader2, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ApiError } from "@/lib/api/client";
import type { UserWorkStatOut, WorkStatsRange } from "@/lib/api/admin_work_stats";
import { cn } from "@/lib/cn";

import StatusBadge from "@/features/jobs/StatusBadge";
import { formatDateTime } from "@/features/jobs/format";
import { useUserJobs, useUserRequests, useWorkStats } from "./useWorkStats";

type Preset = "all" | "7d" | "30d";

/** 오늘 기준 N일 전의 로컬 YYYY-MM-DD. */
function daysAgoIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** 화면 상태(날짜 문자열) → API range. to 는 그날 끝까지 포함하도록 확장. */
function toRange(from: string, to: string): WorkStatsRange {
  return {
    from: from ? `${from}T00:00:00` : undefined,
    to: to ? `${to}T23:59:59` : undefined,
  };
}

export default function WorkStatsPage() {
  const { t } = useTranslation();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const range = useMemo(() => toRange(from, to), [from, to]);
  const { data, isLoading, isError, error, refetch, isFetching } = useWorkStats(range);

  const applyPreset = (preset: Preset) => {
    if (preset === "all") {
      setFrom("");
      setTo("");
    } else {
      setFrom(daysAgoIso(preset === "7d" ? 7 : 30));
      setTo(daysAgoIso(0));
    }
  };

  const activePreset: Preset | null = useMemo(() => {
    if (!from && !to) return "all";
    if (to === daysAgoIso(0) && from === daysAgoIso(7)) return "7d";
    if (to === daysAgoIso(0) && from === daysAgoIso(30)) return "30d";
    return null;
  }, [from, to]);

  const toggleExpand = (row: UserWorkStatOut) => {
    if (row.user_id == null) return; // 삭제된 사용자 — 드릴다운 불가
    setExpandedId((prev) => (prev === row.user_id ? null : row.user_id));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">{t("admin.workStats.title")}</h1>
          <p className="mt-0.5 text-xs text-slate-500">{t("admin.workStats.description")}</p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCw className={cn("h-3.5 w-3.5", isFetching && "animate-spin")} />
          {t("common.actions.refresh")}
        </button>
      </div>

      {/* 기간 필터 */}
      <div className="flex flex-wrap items-end gap-3 rounded-md border border-slate-200 bg-white px-4 py-3">
        <div className="flex items-center gap-1.5">
          {(["all", "7d", "30d"] as Preset[]).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => applyPreset(p)}
              className={cn(
                "rounded-md border px-2.5 py-1 text-xs transition",
                activePreset === p
                  ? "border-blue-500 bg-blue-50 font-medium text-blue-700"
                  : "border-slate-300 text-slate-600 hover:bg-slate-50",
              )}
            >
              {t(`admin.workStats.filter.${p === "all" ? "all" : p === "7d" ? "last7" : "last30"}`)}
            </button>
          ))}
        </div>
        <label className="flex flex-col text-[11px] text-slate-500">
          {t("admin.workStats.filter.from")}
          <input
            type="date"
            value={from}
            max={to || undefined}
            onChange={(e) => setFrom(e.target.value)}
            className="mt-0.5 rounded-md border border-slate-300 px-2 py-1 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </label>
        <label className="flex flex-col text-[11px] text-slate-500">
          {t("admin.workStats.filter.to")}
          <input
            type="date"
            value={to}
            min={from || undefined}
            onChange={(e) => setTo(e.target.value)}
            className="mt-0.5 rounded-md border border-slate-300 px-2 py-1 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </label>
      </div>

      {/* 집계 테이블 */}
      <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="w-8 px-3 py-2" />
              <th className="px-3 py-2">{t("admin.workStats.th.email")}</th>
              <th className="px-3 py-2 text-right">{t("admin.workStats.th.reviewDone")}</th>
              <th className="px-3 py-2 text-right">{t("admin.workStats.th.confirmDone")}</th>
              <th className="px-3 py-2 text-right">{t("admin.workStats.th.total")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-3 py-10 text-center text-xs text-slate-400">
                  <Loader2 className="mx-auto h-4 w-4 animate-spin" aria-hidden />
                </td>
              </tr>
            ) : isError ? (
              <tr>
                <td colSpan={5} className="px-3 py-10 text-center text-xs text-red-500">
                  <span className="inline-flex items-center gap-1.5">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {error instanceof ApiError ? error.detail : (error as Error).message}
                  </span>
                </td>
              </tr>
            ) : !data || data.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-10 text-center text-xs text-slate-400">
                  {t("admin.workStats.empty")}
                </td>
              </tr>
            ) : (
              data.map((row) => {
                const total = row.review_done + row.confirm_done;
                const canExpand = row.user_id != null;
                const isOpen = expandedId === row.user_id && canExpand;
                return (
                  <FragmentRow
                    key={row.user_id ?? `deleted-${row.email}`}
                    row={row}
                    total={total}
                    canExpand={canExpand}
                    isOpen={isOpen}
                    onToggle={() => toggleExpand(row)}
                  />
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** 집계 행 + (펼침 시) 드릴다운 상세 행. */
function FragmentRow({
  row,
  total,
  canExpand,
  isOpen,
  onToggle,
}: {
  row: UserWorkStatOut;
  total: number;
  canExpand: boolean;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        className={cn(
          "text-slate-700",
          canExpand && "cursor-pointer hover:bg-slate-50",
          isOpen && "bg-blue-50/40",
        )}
        onClick={canExpand ? onToggle : undefined}
      >
        <td className="px-3 py-2 text-slate-400">
          {canExpand ? (
            isOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )
          ) : null}
        </td>
        <td className="px-3 py-2">
          <span className={cn("font-medium", row.user_id == null && "italic text-slate-400")}>
            {row.email}
          </span>
        </td>
        <td className="px-3 py-2 text-right tabular-nums">{row.review_done}</td>
        <td className="px-3 py-2 text-right tabular-nums">{row.confirm_done}</td>
        <td className="px-3 py-2 text-right font-semibold tabular-nums text-slate-900">{total}</td>
      </tr>
      {isOpen && row.user_id != null ? (
        <tr>
          <td colSpan={5} className="bg-slate-50/60 px-3 py-3">
            <DrillDown userId={row.user_id} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

/** 드릴다운 — 탭 [의뢰 / 작업]. */
function DrillDown({ userId }: { userId: number }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<"requests" | "jobs">("requests");
  const requests = useUserRequests(userId, tab === "requests");
  const jobs = useUserJobs(userId, tab === "jobs");

  return (
    <div className="rounded-md border border-slate-200 bg-white">
      <div className="flex items-center gap-1 border-b border-slate-200 px-2 py-1.5">
        {(["requests", "jobs"] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={cn(
              "rounded px-2.5 py-1 text-xs transition",
              tab === k
                ? "bg-blue-100 font-medium text-blue-700"
                : "text-slate-600 hover:bg-slate-100",
            )}
          >
            {t(`admin.workStats.tabs.${k}`)}
          </button>
        ))}
      </div>

      <div className="p-2">
        {tab === "requests" ? (
          requests.isLoading ? (
            <LoadingRow />
          ) : requests.isError ? (
            <ErrorRow error={requests.error} />
          ) : !requests.data || requests.data.length === 0 ? (
            <EmptyRow />
          ) : (
            <table className="min-w-full text-xs">
              <thead className="text-left text-[11px] uppercase text-slate-400">
                <tr>
                  <th className="px-2 py-1">{t("admin.workStats.drill.title")}</th>
                  <th className="px-2 py-1">{t("admin.workStats.drill.status")}</th>
                  <th className="px-2 py-1 text-right">{t("admin.workStats.drill.jobs")}</th>
                  <th className="px-2 py-1 text-right">{t("admin.workStats.drill.files")}</th>
                  <th className="px-2 py-1">{t("admin.workStats.drill.createdAt")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {requests.data.map((r) => (
                  <tr key={r.id} className="text-slate-700 hover:bg-slate-50">
                    <td className="px-2 py-1">
                      <Link to={`/requests/${r.id}`} className="text-blue-600 hover:underline">
                        {r.title || `#${r.id.slice(0, 8)}`}
                      </Link>
                    </td>
                    <td className="px-2 py-1">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums">{r.job_count}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{r.file_count}</td>
                    <td className="px-2 py-1 text-slate-500">{formatDateTime(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : jobs.isLoading ? (
          <LoadingRow />
        ) : jobs.isError ? (
          <ErrorRow error={jobs.error} />
        ) : !jobs.data || jobs.data.length === 0 ? (
          <EmptyRow />
        ) : (
          <table className="min-w-full text-xs">
            <thead className="text-left text-[11px] uppercase text-slate-400">
              <tr>
                <th className="px-2 py-1">{t("admin.workStats.drill.name")}</th>
                <th className="px-2 py-1">{t("admin.workStats.drill.status")}</th>
                <th className="px-2 py-1 text-right">{t("admin.workStats.drill.files")}</th>
                <th className="px-2 py-1 text-right">{t("admin.workStats.drill.reviewed")}</th>
                <th className="px-2 py-1">{t("admin.workStats.drill.createdAt")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {jobs.data.map((j) => (
                <tr key={j.id} className="text-slate-700 hover:bg-slate-50">
                  <td className="px-2 py-1">
                    <Link to={`/jobs/${j.id}`} className="text-blue-600 hover:underline">
                      {j.batch_name || `#${j.id.slice(0, 8)}`}
                    </Link>
                  </td>
                  <td className="px-2 py-1">
                    <StatusBadge status={j.status} />
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums">{j.file_count}</td>
                  <td className="px-2 py-1 text-right tabular-nums">
                    {j.reviewed_count}/{j.file_count}
                  </td>
                  <td className="px-2 py-1 text-slate-500">{formatDateTime(j.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function LoadingRow() {
  return (
    <div className="py-6 text-center text-slate-400">
      <Loader2 className="mx-auto h-4 w-4 animate-spin" aria-hidden />
    </div>
  );
}

function EmptyRow() {
  const { t } = useTranslation();
  return (
    <div className="py-6 text-center text-xs text-slate-400">{t("admin.workStats.empty")}</div>
  );
}

function ErrorRow({ error }: { error: unknown }) {
  const msg = error instanceof ApiError ? error.detail : (error as Error).message;
  return <div className="py-6 text-center text-xs text-red-500">{msg}</div>;
}
